import React from 'react';
import { MapPin, Mail, Globe, Landmark } from 'lucide-react';
import { useTranslation } from '../i18n';
import { trpc } from '../lib/trpc';

export const ShopFooter: React.FC = () => {
  const { t } = useTranslation();
  const { data: settings } = trpc.settings.getPublicCompanyInfo.useQuery();
  const { data: buildInfo } = trpc.settings.getBuildInfo.useQuery();
  const company = settings?.company || {};

  const addressLine = [company.addressLine1, company.addressLine2].filter(Boolean).join(', ');
  const cityLine = [company.zip, company.city].filter(Boolean).join(' ');

  const navLinks = [
    { label: t.shop.imprint, href: '/impressum' },
    { label: t.shop.privacyPolicy, href: '/privacy' },
    { label: t.shop.terms, href: '/terms' },
    { label: t.shop.returns, href: '/returns' },
    { label: t.shop.shipping, href: '/shipping' },
    { label: t.shop.payment, href: '/payment' },
    { label: t.shop.contact, href: '/contact' },
  ].filter((link) => Boolean(link.label));

  const contactDetails = [
    addressLine && {
      icon: MapPin,
      text: addressLine,
    },
    cityLine && {
      icon: MapPin,
      text: [cityLine, company.country].filter(Boolean).join(', '),
    },
    company.vatNumber && {
      icon: Landmark,
      text: `VAT: ${company.vatNumber}`,
    },
    company.taxNumber && {
      icon: Landmark,
      text: `Tax: ${company.taxNumber}`,
    },
    company.email && {
      icon: Mail,
      text: company.email,
      href: `mailto:${company.email}`,
    },
    company.website && {
      icon: Globe,
      text: company.website.replace(/^https?:\/\//, ''),
      href: company.website.startsWith('http') ? company.website : `https://${company.website}`,
    },
  ].filter(Boolean) as Array<{ icon: React.ComponentType<{ className?: string }>; text: string; href?: string }>;

  return (
    <footer className="mt-12 border-t border-border/60 bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 py-12 text-sm text-slate-600 dark:text-slate-300">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid gap-8 md:grid-cols-[1.3fr_1fr_1fr]">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{t.shop.imprint}</p>
            {company.companyName && (
              <h3 className="mt-2 text-lg font-semibold text-slate-900 dark:text-white">{company.companyName}</h3>
            )}
            <div className="mt-4 space-y-2 text-sm">
              {addressLine && <p>{addressLine}</p>}
              {cityLine && <p>{cityLine}</p>}
              {company.country && <p>{company.country}</p>}
              {(company.vatNumber || company.taxNumber) && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {[company.vatNumber, company.taxNumber].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
          </div>

          <nav className="flex flex-col gap-3" aria-label="shop-footer-links">
            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{t.shop.terms}</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {navLinks.map((link) => (
                <a key={link.href} className="transition-colors hover:text-slate-900 dark:hover:text-white" href={link.href}>
                  {link.label}
                </a>
              ))}
            </div>
          </nav>

          {contactDetails.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{t.shop.contact}</p>
              <ul className="mt-4 space-y-3">
                {contactDetails.map(({ icon: Icon, text, href }) => (
                  <li key={`${text}-${Icon.name}`} className="flex items-start gap-3">
                    <Icon className="mt-0.5 h-4 w-4 text-slate-400" />
                    {href ? (
                      <a
                        href={href}
                        className="transition-colors hover:text-slate-900 dark:hover:text-white"
                        target={href.startsWith('http') ? '_blank' : undefined}
                        rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      >
                        {text}
                      </a>
                    ) : (
                      <span>{text}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="mt-10 border-t border-white/60 dark:border-white/10 pt-6 text-xs text-slate-500 dark:text-slate-400">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <span>{t.shop.footerDisclaimer}</span>
            <span>
              Build #{(buildInfo as any)?.buildId || '?'}
              {buildInfo?.gitSha && buildInfo.gitSha !== 'unknown' ? ` · ${buildInfo.gitSha}` : ''}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ShopFooter;